import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import apiService from '../services/api';

const titles = {
  request: 'Reset your password',
  reset: 'Set your password',
  verify: 'Verify your email',
  resend: 'Request a verification email',
};

const AccountRecovery = ({ mode }) => {
  const { uid, token } = useParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const isEmailRequest = mode === 'request' || mode === 'resend';

  const submit = async event => {
    event.preventDefault();
    setError('');
    if (mode === 'reset' && (password.length < 8 || password !== confirmation)) {
      setError(password.length < 8 ? 'Use at least 8 characters.' : 'Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const endpoints = {
        request: '/api/password-reset/',
        reset: '/api/password-reset/confirm/',
        verify: '/api/verify-email/',
        resend: '/api/verify-email/resend/',
      };
      const payload = isEmailRequest
        ? { email: email.trim() }
        : mode === 'reset'
          ? { uid, token, password }
          : { token };
      const response = await apiService.client.post(endpoints[mode], payload);
      setMessage(
        response.data.detail ||
          (mode === 'reset'
            ? 'Your password has been saved. You can now sign in.'
            : 'Request completed.')
      );
      setPassword('');
      setConfirmation('');
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          'We could not complete this request. Check your connection and try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="account-page">
      <h1 className="account-title">{titles[mode]}</h1>
      <p className="account-description mb-6">
        {isEmailRequest
          ? 'Enter the email address for your account.'
          : mode === 'reset'
            ? 'Choose a password for your invited or existing account.'
            : 'Confirm this address to complete your account setup.'}
      </p>
      <div className="account-form">
        {message ? (
          <div role="status" className="space-y-4">
            <p>{message}</p>
            <Link to="/login" className="btn-primary btn-md">
              Continue to sign in
            </Link>
            {isEmailRequest && (
              <button className="btn-ghost btn-md" type="button" onClick={() => setMessage('')}>
                Use a different email or try again
              </button>
            )}
          </div>
        ) : (
          <form onSubmit={submit} aria-busy={busy} className="space-y-5">
            {error && (
              <p id="account-error" role="alert" className="alert-error p-4">
                {error}
              </p>
            )}
            {isEmailRequest && (
              <div>
                <label htmlFor="account-email" className="block font-semibold mb-2">
                  Email address
                </label>
                <input
                  id="account-email"
                  type="email"
                  required
                  autoComplete="email"
                  className="input"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  aria-describedby={error ? 'account-error' : undefined}
                />
              </div>
            )}
            {mode === 'reset' && (
              <>
                <div>
                  <label htmlFor="new-password" className="block font-semibold mb-2">
                    New password
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="input"
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    aria-describedby={`password-help${error ? ' account-error' : ''}`}
                  />
                  <p id="password-help" className="text-sm text-gray-600 mt-2">
                    Use at least 8 characters.
                  </p>
                </div>
                <div>
                  <label htmlFor="confirm-new-password" className="block font-semibold mb-2">
                    Confirm password
                  </label>
                  <input
                    id="confirm-new-password"
                    type="password"
                    required
                    autoComplete="new-password"
                    className="input"
                    value={confirmation}
                    onChange={event => setConfirmation(event.target.value)}
                    aria-describedby={error ? 'account-error' : undefined}
                  />
                </div>
              </>
            )}
            <button type="submit" disabled={busy} className="btn-primary btn-lg w-full">
              {busy
                ? 'Submitting…'
                : isEmailRequest
                  ? 'Send email'
                  : mode === 'reset'
                    ? 'Save password'
                    : 'Verify email'}
            </button>
            {(mode === 'reset' || mode === 'verify') && (
              <p className="text-sm text-gray-600">
                Link expired or already used?{' '}
                <Link
                  className="underline"
                  to={mode === 'reset' ? '/forgot-password' : '/verify-email'}
                >
                  Request a new link
                </Link>
                .
              </p>
            )}
          </form>
        )}
      </div>
      {!message && (
        <Link to="/login" className="workspace-link mt-4">
          Back to sign in
        </Link>
      )}
    </div>
  );
};

export default AccountRecovery;
